import type { RefObject, SyntheticEvent } from 'react';
import { useUID } from 'react-uid';
import styled from 'styled-components';
import type {
  AccountUser,
  SuggestedAccountUser,
  SuggestedAccountUserGroup,
  SuggestedMember,
  SuggestedShareList,
} from '@frameio/graphql-client/generated';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import { imageLoader } from '@frameio/utils';
import { Box, isFunction, Text, type ThemeSpaces } from '@frameio/vapor';
import TEXT from './generated/TEXT/MemberAccessRowList';
import { useKeyInteraction } from './hooks/useKeyInteraction';
import { MemberAccessRow } from './MemberAccessRow';
import { PendingMemberAccessRow } from './PendingMemberAccessRow';
import { ShareListAccessRow } from './ShareListAccessRow';
import { isSelectable } from './utils/isSelectable';
import { unpackMaybe } from './utils/unpackMaybe';

TEXT.DEFINE({
  /** Group heading for account members and groups */
  membersHeading: 'Members',
  /** Group heading for share lists */
  shareListsHeading: 'Share Lists',
});

const StyledUl = styled.ul`
  list-style: none;
`;

type MemberAccessRowListProps = {
  controlledByElement: HTMLElement;
  highlightFirstRow: boolean;
  label: string;
  matches: Array<SuggestedMember | AccountUser>;
  onMemberClick: (evt: SyntheticEvent<HTMLButtonElement>) => void;
  onPendingMemberClick?: (textInput: string) => void;
  paddingBottom?: ThemeSpaces;
  paddingX?: ThemeSpaces;
  showGroupHeadings?: boolean;
  textInput: string;
};

type MemberAccessRowEntry = SuggestedMember | AccountUser;

type GroupedMatches = {
  members: Array<MemberAccessRowEntry>;
  shareLists: Array<SuggestedShareList>;
};

type GroupedMatchSection = {
  entries: Array<MemberAccessRowEntry>;
  heading: string;
  headingId: string;
};

export function MemberAccessRowList({
  controlledByElement,
  highlightFirstRow,
  label,
  matches,
  onMemberClick,
  onPendingMemberClick,
  paddingBottom = 4,
  paddingX = 4,
  showGroupHeadings = false,
  textInput,
}: MemberAccessRowListProps) {
  const uid = useUID();
  const currentUserId = useGetActiveUserId();
  const [listRef, activeItemId] = useKeyInteraction(controlledByElement);

  const groupedMatches = showGroupHeadings
    ? matches.reduce<GroupedMatches>(
        (groups, entry) => {
          if (isShareList(entry)) {
            groups.shareLists.push(entry);
          } else {
            groups.members.push(entry);
          }

          return groups;
        },
        {
          members: [],
          shareLists: [],
        }
      )
    : undefined;

  const groupedMatchSections: Array<GroupedMatchSection> = groupedMatches
    ? (
        [
          {
            entries: groupedMatches.members,
            heading: TEXT.membersHeading,
            headingId: `members-heading-${uid}`,
          },
          {
            entries: groupedMatches.shareLists,
            heading: TEXT.shareListsHeading,
            headingId: `share-lists-heading-${uid}`,
          },
        ] satisfies Array<GroupedMatchSection>
      ).filter(({ entries }) => entries.length > 0)
    : [];

  const firstSelectableGroupedEntry: MemberAccessRowEntry | undefined =
    groupedMatches?.members.find(isSelectable) ??
    groupedMatches?.shareLists.find(isSelectable);

  const pendingItemId = `suggested-pending-${uid}`;

  function renderEntry(entry: MemberAccessRowEntry, highlight: boolean) {
    const itemId = `suggested-${entry.id}`;
    const isActive = activeItemId === itemId;

    const entryAsGroup =
      (entry as SuggestedAccountUserGroup).__typename ===
      'SuggestedAccountUserGroup'
        ? (entry as SuggestedAccountUserGroup)
        : undefined;

    const entryAsAccountUser = entryAsGroup
      ? undefined
      : (entry as SuggestedAccountUser | AccountUser);

    let profileImage;
    let avatarBackground;
    if (entryAsAccountUser?.__typename === 'SuggestedAccountUser') {
      profileImage = entryAsAccountUser?.profileImage?.image64;
      avatarBackground = entryAsAccountUser?.avatarColor;
    } else if (entryAsAccountUser?.__typename === 'AccountUser') {
      profileImage =
        entryAsAccountUser?.profileImageUrl &&
        imageLoader({
          src: entryAsAccountUser?.profileImageUrl,
          width: 64,
        });
      avatarBackground = entryAsAccountUser?.avatarColor;
    }

    const hasAccess = !isSelectable(entry);

    return (
      <Box
        key={entry.id}
        aria-selected={isActive}
        as="li"
        id={itemId}
        paddingBottom={paddingBottom}
        paddingX={paddingX}
        role="option"
      >
        {isShareList(entry) ? (
          <ShareListAccessRow
            avatarIcons={entry.avatarIcons}
            creatorName={entry.creator?.name ?? undefined}
            highlight={highlight}
            isCreatedByCurrentUser={Boolean(
              entry.creator?.id && entry.creator.id === currentUserId
            )}
            memberCount={entry.memberCount ?? undefined}
            name={entry.name ?? ''}
            onClick={onMemberClick}
            shareListId={entry.id}
            textInput={textInput}
          />
        ) : (
          <MemberAccessRow
            avatarBackground={unpackMaybe(avatarBackground)}
            avatarUrl={profileImage ?? ''}
            emoji={unpackMaybe(entryAsGroup?.emoji)}
            hasAccess={hasAccess}
            highlight={highlight}
            isGroup={Boolean(entryAsGroup)}
            isGuest={Boolean(entryAsAccountUser?.licenseType === 'GUEST')}
            isPending={entryAsAccountUser?.isPending}
            memberCount={unpackMaybe(entryAsGroup?.memberCount)}
            memberEmail={
              entryAsAccountUser?.email ||
              entryAsAccountUser?.inviteEmailAddress ||
              ''
            }
            memberId={entry.id}
            memberName={entryAsAccountUser?.name || entryAsGroup?.name || ''}
            onClick={onMemberClick}
            textInput={textInput}
            truncateEmail={true}
          />
        )}
      </Box>
    );
  }

  return (
    <StyledUl
      ref={listRef as unknown as RefObject<HTMLUListElement>}
      aria-label={label}
      role="listbox"
    >
      {showGroupHeadings ? (
        <>
          {groupedMatchSections.map(({ entries, heading, headingId }) => (
            <li key={headingId} aria-labelledby={headingId} role="group">
              <Box paddingBottom={2} paddingTop={8} paddingX={paddingX}>
                <Text color="tertiary" id={headingId} variant="caption">
                  {heading}
                </Text>
              </Box>
              <StyledUl role="presentation">
                {entries.map((entry) =>
                  renderEntry(
                    entry,
                    entry === firstSelectableGroupedEntry && highlightFirstRow
                  )
                )}
              </StyledUl>
            </li>
          ))}
        </>
      ) : (
        matches.map((entry, index) =>
          renderEntry(entry, index === 0 && highlightFirstRow)
        )
      )}
      {isFunction(onPendingMemberClick) &&
        textInput &&
        matches.length === 0 && (
          <Box
            key={textInput}
            aria-selected={activeItemId === pendingItemId}
            as="li"
            id={pendingItemId}
            onClick={() => onPendingMemberClick(textInput)}
            /* Match the suggestion rows: keep input focus on click so the row's
               onClick is the sole commit path. Without this, the click also blurs
               the input, which commits the same email a second time. */
            onMouseDown={(e) => e.preventDefault()}
            paddingBottom={4}
            paddingX={4}
            role="option"
          >
            <PendingMemberAccessRow pendingMemberEmail={textInput} />
          </Box>
        )}
    </StyledUl>
  );
}

function isShareList(entry: unknown): entry is SuggestedShareList {
  if (typeof entry === 'object' && entry) {
    return '__typename' in entry && entry.__typename === 'SuggestedShareList';
  }

  return false;
}
